import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useProfileQuery } from "@/hooks/profile/useProfileQuery";
import { UserListHeader } from "@/components/admin/users/UserListHeader";
import { UserListContainer } from "@/components/admin/users/UserListContainer";
import { UserFilters, UserFiltersState } from "@/components/admin/users/UserFilters";
import { useUsersQuery } from "@/hooks/admin/useUsersQuery";
import { useUserMutations } from "@/hooks/admin/useUserMutations";
import { useUserPayments } from "@/hooks/admin/useUserPayments";
import { useUserDebts } from "@/hooks/admin/useMemberDebts";
import { useFilteredUsers } from "@/hooks/admin/useFilteredUsers";
import { Profile } from "@/hooks/profile/types";
import { useQueryClient, useQuery } from "@tanstack/react-query";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AttendanceTab } from "@/components/admin/attendance";
import { supabase } from "@/integrations/supabase/client";
import { useRecepcionistaRedirect } from "@/hooks/useRecepcionistaRedirect";

const TotalUsuarios = () => {
  const [searchParams] = useSearchParams();
  const gymId = searchParams.get("gym_id");
  const [isUpdating, setIsUpdating] = useState(false);
  const [activeTab, setActiveTab] = useState("users");
  const [filters, setFilters] = useState<UserFiltersState>({
    search: "",
    role: "all",
    status: "all",
    paymentStatus: "all",
  });
  const queryClient = useQueryClient();
  
  const { data: currentUser } = useProfileQuery() as { data: Profile };
  // "Acts as recepcionista" — true for pure recepcionistas AND dual-role profes
  // (e.g. Sergio/Agustín de MASTERGYM) que tienen el rol en user_roles.
  const { isRecepcionista: actsAsRecepcionista } = useRecepcionistaRedirect();
  const isRecepcionista = !!actsAsRecepcionista;
  // Recepcionista doesn't pass gym_id via URL — use their profile gym_id
  const effectiveGymIdForQuery = gymId || (isRecepcionista ? currentUser?.gym_id ?? null : null);
  const { data: users, isLoading } = useUsersQuery(effectiveGymIdForQuery, currentUser);
  
  const userIds = users?.map((u) => u.id) || [];
  const { data: paymentStatusMap } = useUserPayments(userIds);
  
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 20;

  const filteredUsers = useFilteredUsers(
    users || null,
    filters,
    paymentStatusMap || new Map()
  );

  // Reset page when filters change
  useEffect(() => setCurrentPage(1), [filters]);

  const safeTotalPages = Math.max(1, Math.ceil(filteredUsers.length / pageSize));
  const safePage = Math.min(Math.max(1, currentPage), safeTotalPages);
  const paginatedUsers = filteredUsers.slice(
    (safePage - 1) * pageSize,
    safePage * pageSize
  );

  const { handleBanUser, handleRoleChange } = useUserMutations();

  const onBanUser = async (userId: string, isBanned: boolean) => {
    setIsUpdating(true);
    await handleBanUser(userId, isBanned);
    setIsUpdating(false);
  };

  const onRoleChange = async (userId: string, newRole: string) => {
    setIsUpdating(true);
    await handleRoleChange(userId, newRole);
    setIsUpdating(false);
  };

  const handleUserAdded = () => {
    queryClient.invalidateQueries({ queryKey: ["users"] });
  };

  const effectiveGymId = gymId || currentUser?.gym_id;

  // Fetch gym name for AMARELO detection
  const { data: gymData } = useQuery({
    queryKey: ["gym", effectiveGymId],
    queryFn: async () => {
      if (!effectiveGymId) return null;
      const { data } = await supabase
        .from("gyms")
        .select("name")
        .eq("id", effectiveGymId)
        .single();
      return data;
    },
    enabled: !!effectiveGymId,
  });

  // Check if current gym is Amarelo by name (more robust than ID)
  const hasAttendanceFeature = gymData?.name?.toUpperCase().includes("AMARELO") || gymData?.name?.toUpperCase().includes("BERROS") || gymData?.name?.toUpperCase().includes("MASTERGYM") || gymData?.name?.toUpperCase().includes("NEW BODY") || false;
  const isMastergym = gymData?.name?.toUpperCase().includes("MASTERGYM") ?? false;
  const { data: debtsMap } = useUserDebts(isMastergym ? effectiveGymId : null, isMastergym ? userIds : []);

  return (
    <div className="flex flex-col h-screen w-screen bg-background text-primary mx-auto pt-8 sm:pt-16 px-2 sm:px-6 p-0">
      <UserListHeader gymId={gymId || (isRecepcionista ? currentUser?.gym_id ?? null : null)} onUserAdded={handleUserAdded} showAddUser={!hasAttendanceFeature || activeTab === "users"} />
      
      {hasAttendanceFeature ? (
        <Tabs defaultValue="users" onValueChange={setActiveTab} className="flex-1 flex flex-col">
          <TabsList className="w-fit mb-4">
            <TabsTrigger value="users">Usuarios</TabsTrigger>
            <TabsTrigger value="attendance">Asistencia</TabsTrigger>
          </TabsList>
          
          <TabsContent value="users" className="flex-1 flex flex-col mt-0">
            <UserFilters filters={filters} onFiltersChange={setFilters} />
            <UserListContainer
              currentUser={currentUser as Profile}
              users={paginatedUsers}
              totalUsers={users?.length || 0}
              filteredCount={filteredUsers.length}
              isLoading={isLoading}
              isUpdating={isUpdating}
              onBanUser={onBanUser}
              onRoleChange={onRoleChange}
              currentPage={safePage}
              totalPages={safeTotalPages}
              onPageChange={setCurrentPage}
              paymentStatusMap={paymentStatusMap || new Map()}
              actsAsRecepcionista={isRecepcionista}
              isMastergym={isMastergym}
              debtsMap={debtsMap}
            />
          </TabsContent>
          
          <TabsContent value="attendance" className="mt-0">
            {effectiveGymId && (
              <AttendanceTab gymId={effectiveGymId} />
            )}
          </TabsContent>
        </Tabs>
      ) : (
        <>
          <UserFilters filters={filters} onFiltersChange={setFilters} />
          <UserListContainer
            currentUser={currentUser as Profile}
            users={paginatedUsers}
            totalUsers={users?.length || 0}
            filteredCount={filteredUsers.length}
            isLoading={isLoading}
            isUpdating={isUpdating}
            onBanUser={onBanUser}
            onRoleChange={onRoleChange}
            currentPage={safePage}
            totalPages={safeTotalPages}
            onPageChange={setCurrentPage}
            paymentStatusMap={paymentStatusMap || new Map()}
            actsAsRecepcionista={isRecepcionista}
            isMastergym={isMastergym}
            debtsMap={debtsMap}
          />
        </>
      )}
    </div>
  );
};

export default TotalUsuarios;
